import TreasuryTable from '../TreasuryTable';
import React, { useEffect, useState } from 'react';
import './AletaBillingTab.css';
import { can } from '../../../utils/permissions';
import { hasAletaBillingFeature } from '../../../utils/colla-features';
import { COLLA } from '../../../utils/utils';
import { CADENCE_LABELS, createClientForm, getDefaultAletaBillingCadenceForColla } from './constants';
import { downloadAletaInvoicePdf, fetchAletaBillingOverview, issueAletaInvoice } from './api';
import { calculateBillingPeriodTotal, formatCurrency, formatDate, formatPeriodLabel } from './formatters';
import StatusCard from './StatusCard';
import IssueInvoiceModal from './IssueInvoiceModal';

function AletaBillingTab({ userInfo, userId }) {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [issueError, setIssueError] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [issuing, setIssuing] = useState(false);
    const [downloadingInvoice, setDownloadingInvoice] = useState(null);
    const [issueModal, setIssueModal] = useState({
        visible: false,
        period: null,
        clientForm: createClientForm(),
    });

    const hasAccess = can(userInfo, 'ACCESS_ALETA_BILLING');
    const featureEnabled = hasAletaBillingFeature();
    const canIssueInvoices = COLLA === 'nova';

    const loadOverview = async ({ keepState = false } = {}) => {
        if (!userId || !hasAccess || !featureEnabled) {
            return;
        }

        if (!keepState) {
            setLoading(true);
        }
        setError(null);

        try {
            const payload = await fetchAletaBillingOverview(userId);
            setOverview(payload);
        } catch (loadError) {
            setError(loadError.message || 'No s\'ha pogut carregar la facturació d\'Aleta.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOverview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, hasAccess, featureEnabled]);

    const openIssueModal = (period) => {
        if (!canIssueInvoices) {
            return;
        }

        setIssueError(null);
        setStatusMessage(null);
        setIssueModal({
            visible: true,
            period,
            clientForm: createClientForm(overview?.clientProfileBase),
        });
    };

    const closeIssueModal = () => {
        if (issuing) {
            return;
        }

        setIssueError(null);
        setIssueModal({
            visible: false,
            period: null,
            clientForm: createClientForm(),
        });
    };

    const updateClientField = (field, value) => {
        setIssueModal(previous => ({
            ...previous,
            clientForm: {
                ...previous.clientForm,
                [field]: value,
            },
        }));
    };

    const handleIssueInvoice = async () => {
        if (!issueModal.period) {
            return;
        }

        setIssuing(true);
        setIssueError(null);
        setStatusMessage(null);

        try {
            await issueAletaInvoice({
                userId,
                periodKey: issueModal.period.periodKey,
                clientOverrides: issueModal.clientForm,
            });

            setStatusMessage({
                tone: 'success',
                text: `Factura ${issueModal.period.label} emesa correctament.`,
            });
            setIssueModal({
                visible: false,
                period: null,
                clientForm: createClientForm(),
            });
            await loadOverview({ keepState: true });
        } catch (submitError) {
            const missingFields = submitError?.payload?.missingClientFields;
            if (Array.isArray(missingFields) && missingFields.length > 0) {
                setIssueError(`${submitError.message} Falten: ${missingFields.join(', ')}.`);
            } else {
                setIssueError(submitError.message || 'No s\'ha pogut emetre la factura.');
            }
        } finally {
            setIssuing(false);
        }
    };

    const handleDownloadInvoice = async (invoiceNumber) => {
        setDownloadingInvoice(invoiceNumber);
        setStatusMessage(null);

        try {
            await downloadAletaInvoicePdf({ userId, invoiceNumber });
        } catch (downloadError) {
            setStatusMessage({
                tone: 'error',
                text: downloadError.message || 'No s\'ha pogut descarregar la factura.',
            });
        } finally {
            setDownloadingInvoice(null);
        }
    };

    if (!featureEnabled) {
        return (
            <div className="aleta-billing-tab">
                <div className="aleta-billing-empty-state">
                    <h2>Factures Aleta</h2>
                    <p>La facturació d&apos;Aleta no està disponible per a colles universitàries.</p>
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="aleta-billing-tab">
                <div className="aleta-billing-empty-state">
                    <h2>Factures Aleta</h2>
                    <p>No tens permís per accedir a la facturació d&apos;Aleta.</p>
                </div>
            </div>
        );
    }

    if (loading && !overview) {
        return (
            <div className="aleta-billing-tab">
                <div className="aleta-billing-loading">
                    <div className="aleta-billing-spinner"></div>
                    <p>Carregant factures d&apos;Aleta...</p>
                </div>
            </div>
        );
    }

    if (error && !overview) {
        return (
            <div className="aleta-billing-tab">
                <div className="aleta-billing-empty-state">
                    <h2>Factures Aleta</h2>
                    <p>{error}</p>
                    <button className="aleta-billing-primary-button" onClick={() => loadOverview()}>
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    const config = overview?.config || null;
    const defaultCadence = overview?.defaultCadence || getDefaultAletaBillingCadenceForColla(COLLA);
    const displayCadence = config?.billingCadence || defaultCadence;
    const pendingPeriods = overview?.pendingPeriods || [];
    const invoices = overview?.invoices || [];
    const missingClientFields = overview?.missingClientFields || [];

    return (
        <div className="aleta-billing-tab">
            <div className="aleta-billing-header">
                <div>
                    <h2>Factures Aleta</h2>
                    <p>
                        Genera i descarrega les factures del servei Aleta amb la sèrie pròpia de la colla.
                    </p>
                </div>
                <button className="aleta-billing-secondary-button" onClick={() => loadOverview()}>
                    Actualitzar
                </button>
            </div>

            {statusMessage ? (
                <div className={`aleta-billing-alert ${statusMessage.tone}`}>
                    {statusMessage.text}
                </div>
            ) : null}
            {error ? (
                <div className="aleta-billing-alert error">
                    {error}
                </div>
            ) : null}
            {!canIssueInvoices ? (
                <div className="aleta-billing-alert">
                    L&apos;emissió de factures d&apos;Aleta està bloquejada per a aquesta colla. Només està habilitada per a la colla Nova.
                </div>
            ) : null}

            <div className="aleta-billing-status-grid">
                <StatusCard
                    title="Pla"
                    value={displayCadence ? CADENCE_LABELS[displayCadence] || displayCadence : 'No configurat'}
                    tone={config ? 'ready' : (displayCadence ? 'neutral' : 'warning')}
                    hint={config?.billingStartDate
                        ? `Inici: ${formatDate(config.billingStartDate)}`
                        : displayCadence
                            ? `Cadència per defecte: ${(CADENCE_LABELS[displayCadence] || displayCadence).toLowerCase()}.`
                            : 'Cal configurar la facturació d\'Aleta'}
                />
                <StatusCard
                    title="Prefix"
                    value={config?.invoicePrefix || 'Sense prefix'}
                    tone={config?.invoicePrefix ? 'ready' : 'warning'}
                    hint={config ? 'Sèrie de factures per colla' : 'No hi ha sèrie activa'}
                />
                <StatusCard
                    title="Total colla"
                    value={config ? formatCurrency(calculateBillingPeriodTotal(config.monthlyBaseAmount, config.billingCadence)) : '-'}
                    tone={config ? 'ready' : 'neutral'}
                    hint={config ? 'Import final del període per a la colla' : 'No disponible'}
                />
                <StatusCard
                    title="Dades fiscals"
                    value={missingClientFields.length === 0 ? 'Completes' : 'Pendents'}
                    tone={missingClientFields.length === 0 ? 'ready' : 'warning'}
                    hint={missingClientFields.length === 0 ? 'Es poden revisar abans d\'emetre' : `Falten: ${missingClientFields.join(', ')}`}
                />
            </div>

            {!config ? (
                <div className="aleta-billing-panel aleta-billing-empty-state">
                    <h3>Pla no configurat</h3>
                    <p>
                        Encara no hi ha cap configuració activa per a aquesta colla.
                        {displayCadence ? ` La cadència per defecte és ${(CADENCE_LABELS[displayCadence] || displayCadence).toLowerCase()}.` : ''}
                        {' '}Quan Aleta activi el prefix i l&apos;import base, aquí hi apareixeran els períodes pendents.
                    </p>
                </div>
            ) : null}

            {config && missingClientFields.length > 0 ? (
                <div className="aleta-billing-panel aleta-billing-warning-panel">
                    <h3>Dades fiscals incompletes</h3>
                    <p>
                        La base actual de quotes encara no està completa. Pots corregir-ho des de la pestanya de dades fiscals o bé completar-ho només per a la factura que vols emetre.
                    </p>
                </div>
            ) : null}

            <div className="aleta-billing-columns">
                <section className="aleta-billing-panel">
                    <div className="aleta-billing-section-header">
                        <div>
                            <h3>Períodes pendents</h3>
                            <p>Períodes que encara no tenen cap factura emesa.</p>
                        </div>
                        <span className="aleta-billing-count-pill">{pendingPeriods.length}</span>
                    </div>

                    {pendingPeriods.length === 0 ? (
                        <div className="aleta-billing-empty-state compact">
                            <p>No hi ha cap període pendent ara mateix.</p>
                        </div>
                    ) : (
                        <div className="aleta-billing-list">
                            {pendingPeriods.map(period => (
                                <div key={period.periodKey} className="aleta-billing-period-card">
                                    <div className="aleta-billing-period-main">
                                        <div className="aleta-billing-period-title">{period.label}</div>
                                        <div className="aleta-billing-period-dates">
                                            {formatDate(period.periodStart)} - {formatDate(period.periodEnd)}
                                        </div>
                                    </div>
                                    <div className="aleta-billing-period-summary">
                                        <span>{formatCurrency(period.amounts.baseAmount)} base</span>
                                        <span>{formatCurrency(period.amounts.totalAmount)} total</span>
                                    </div>
                                    <button
                                        className="aleta-billing-primary-button"
                                        onClick={() => openIssueModal(period)}
                                        disabled={!canIssueInvoices}
                                    >
                                        {missingClientFields.length > 0 ? 'Completar i emetre' : 'Revisar i emetre'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <section className="aleta-billing-panel">
                    <div className="aleta-billing-section-header">
                        <div>
                            <h3>Factures emeses</h3>
                            <p>Històric disponible per descarregar.</p>
                        </div>
                        <span className="aleta-billing-count-pill">{invoices.length}</span>
                    </div>

                    {invoices.length === 0 ? (
                        <div className="aleta-billing-empty-state compact">
                            <p>Encara no s&apos;ha emès cap factura d&apos;Aleta.</p>
                        </div>
                    ) : (
                        <TreasuryTable label="Factures Aleta" className="aleta-billing-table-wrapper">
                            <table className="aleta-billing-table">
                                <thead>
                                    <tr>
                                        <th>Número</th>
                                        <th>Període</th>
                                        <th>Emesa</th>
                                        <th>Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map(invoice => (
                                        <tr key={invoice.invoiceNumber}>
                                            <td className="aleta-billing-table-strong">{invoice.invoiceNumber}</td>
                                            <td>{formatPeriodLabel(invoice)}</td>
                                            <td>{formatDate(invoice.issuedAt, true)}</td>
                                            <td>{formatCurrency(invoice.totalAmount)}</td>
                                            <td className="aleta-billing-table-actions">
                                                <button
                                                    className="aleta-billing-secondary-button"
                                                    onClick={() => handleDownloadInvoice(invoice.invoiceNumber)}
                                                    disabled={downloadingInvoice === invoice.invoiceNumber}
                                                >
                                                    {downloadingInvoice === invoice.invoiceNumber ? 'Descarregant...' : 'Descarregar PDF'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </TreasuryTable>
                    )}
                </section>
            </div>

            <IssueInvoiceModal
                visible={issueModal.visible}
                period={issueModal.period}
                clientForm={issueModal.clientForm}
                issuerProfile={overview?.issuerProfile}
                issuing={issuing}
                issueError={issueError}
                onClose={closeIssueModal}
                onSubmit={handleIssueInvoice}
                onFieldChange={updateClientField}
            />
        </div>
    );
}

export default AletaBillingTab;
